/**
 * DepartmentFormModal Component
 * Modal for adding and editing departments
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { SearchableSelect } from '../ui/SearchableSelect';
import { Button } from '../ui/Button';
import { departmentsService, type DepartmentFormData } from '../../services/departments.service';
import { membersService } from '../../services/members.service';
import { useToast } from '../../hooks/useToast';

export interface DepartmentFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'add' | 'edit';
  departmentId?: number;
}

export const DepartmentFormModal = ({ isOpen, onClose, mode, departmentId }: DepartmentFormModalProps) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<DepartmentFormData>({
    name: '',
    description: '',
  });

  const [departmentLogo, setDepartmentLogo] = useState<File | null>(null);
  const [currentImageUrl, setCurrentImageUrl] = useState<string>('');
  const [selectedManagerIds, setSelectedManagerIds] = useState<number[]>([]);
  const [selectedDirectorIds, setSelectedDirectorIds] = useState<number[]>([]);

  // Department name validation state
  const [nameError, setNameError] = useState<string | null>(null);
  const [isCheckingName, setIsCheckingName] = useState(false);
  const nameCheckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const originalNameRef = useRef<string | null>(null);

  // Fetch members for manager picker
  const { data: members = [] } = useQuery({
    queryKey: ['members'],
    queryFn: membersService.getAll,
  });

  // Fetch department data for edit mode
  const { data: department } = useQuery({
    queryKey: ['department', departmentId],
    queryFn: () => departmentsService.getById(departmentId!),
    enabled: mode === 'edit' && !!departmentId,
  });

  // Populate form when department data loads
  useEffect(() => {
    if (isOpen && mode === 'edit' && department) {
      setFormData({
        name: department.name,
        description: department.description || '',
      });
      originalNameRef.current = department.name;
      // Load existing managers and directors
      if (department.managers) {
        setSelectedManagerIds(department.managers.map((m) => m.member_id));
      }
      if (department.directors) {
        setSelectedDirectorIds(department.directors.map((d) => d.member_id));
      }
    }
  }, [isOpen, mode, department]);

  // Load department logo as blob with JWT authentication
  useEffect(() => {
    let mounted = true;

    const loadImage = async () => {
      if (isOpen && mode === 'edit' && department && department.has_department_logo) {
        try {
          const blobUrl = await departmentsService.fetchDepartmentImage(department.id);
          if (blobUrl && mounted) {
            setCurrentImageUrl(blobUrl);
          }
        } catch (error) {
          console.error(`Failed to load image for department ${department.id}:`, error);
        }
      }
    };

    loadImage();

    return () => {
      mounted = false;
    };
  }, [isOpen, mode, department]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({ name: '', description: '' });
      setDepartmentLogo(null);
      setCurrentImageUrl('');
      setSelectedManagerIds([]);
      setSelectedDirectorIds([]);
      setNameError(null);
      originalNameRef.current = null;

      if (nameCheckTimeoutRef.current) {
        clearTimeout(nameCheckTimeoutRef.current);
      }

      if (mode === 'edit' && departmentId) {
        departmentsService.revokeDepartmentImage(departmentId);
      }
    }
  }, [isOpen, mode, departmentId]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: DepartmentFormData) => departmentsService.create(data),
    onSuccess: async () => {
      toast.success('Department created successfully');

      if (departmentLogo) {
        departmentsService.clearDepartmentImageCache();
      }

      await queryClient.refetchQueries({ queryKey: ['departments'] });
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to create department');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: DepartmentFormData) => departmentsService.update(departmentId!, data),
    onSuccess: async () => {
      toast.success('Department updated successfully');

      if (departmentId) {
        departmentsService.revokeDepartmentImage(departmentId);
      }

      await queryClient.refetchQueries({ queryKey: ['departments'] });
      await queryClient.refetchQueries({ queryKey: ['department', departmentId] });
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update department');
    },
  });

  // Handle name change
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData((prev) => ({ ...prev, name: value }));
    if (nameError) {
      setNameError(null);
    }
  };

  // Handle name blur - check for duplicates
  const handleNameBlur = useCallback(async () => {
    const name = formData.name.trim();

    if (!name) {
      setNameError(null);
      return;
    }

    if (mode === 'edit' && name.toLowerCase() === originalNameRef.current?.toLowerCase()) {
      setNameError(null);
      return;
    }

    if (nameCheckTimeoutRef.current) {
      clearTimeout(nameCheckTimeoutRef.current);
    }

    setIsCheckingName(true);
    nameCheckTimeoutRef.current = setTimeout(async () => {
      try {
        const result = await departmentsService.checkNameExists(
          name,
          mode === 'edit' ? departmentId : undefined
        );
        if (result.exists) {
          setNameError('Department name already exists');
        } else {
          setNameError(null);
        }
      } catch {
        setNameError(null);
      } finally {
        setIsCheckingName(false);
      }
    }, 500);
  }, [formData.name, mode, departmentId]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        e.target.value = '';
        return;
      }

      if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
        toast.error('File must be JPG or PNG');
        e.target.value = '';
        return;
      }

      setDepartmentLogo(file);
    }
  };

  // When admin picks a member already chosen for the other role, prompt for confirmation.
  // Same UX as the detail pages — explicit decision, not a silent swap.
  const [pendingSwap, setPendingSwap] = useState<{
    memberId: number;
    memberName: string;
    from: 'director' | 'manager';
    to: 'director' | 'manager';
  } | null>(null);

  const handleAddManager = (memberIdStr: string) => {
    if (!memberIdStr) return;
    const id = Number(memberIdStr);
    if (selectedDirectorIds.includes(id)) {
      const m = members.find((x) => x.id === id);
      if (m) {
        setPendingSwap({
          memberId: id,
          memberName: `${m.first_name} ${m.last_name}`,
          from: 'director',
          to: 'manager',
        });
      }
      return;
    }
    if (!selectedManagerIds.includes(id)) {
      setSelectedManagerIds((prev) => [...prev, id]);
    }
  };
  const handleRemoveManager = (memberId: number) => {
    setSelectedManagerIds((prev) => prev.filter((id) => id !== memberId));
  };

  const handleAddDirector = (memberIdStr: string) => {
    if (!memberIdStr) return;
    const id = Number(memberIdStr);
    if (selectedManagerIds.includes(id)) {
      const m = members.find((x) => x.id === id);
      if (m) {
        setPendingSwap({
          memberId: id,
          memberName: `${m.first_name} ${m.last_name}`,
          from: 'manager',
          to: 'director',
        });
      }
      return;
    }
    if (!selectedDirectorIds.includes(id)) {
      setSelectedDirectorIds((prev) => [...prev, id]);
    }
  };
  const handleRemoveDirector = (memberId: number) => {
    setSelectedDirectorIds((prev) => prev.filter((id) => id !== memberId));
  };

  const confirmSwap = () => {
    if (!pendingSwap) return;
    const { memberId, from, to } = pendingSwap;
    setPendingSwap(null);
    if (from === 'director') {
      setSelectedDirectorIds((prev) => prev.filter((id) => id !== memberId));
    } else {
      setSelectedManagerIds((prev) => prev.filter((id) => id !== memberId));
    }
    if (to === 'director') {
      setSelectedDirectorIds((prev) => prev.includes(memberId) ? prev : [...prev, memberId]);
    } else {
      setSelectedManagerIds((prev) => prev.includes(memberId) ? prev : [...prev, memberId]);
    }
  };

  // Picker candidates exclude only the same-role selections; cross-role members stay
  // selectable so admin can opt into the swap flow via the confirmation modal.
  const activeMembers = members.filter((m) => !m.removed_at);
  const availableManagerMembers = activeMembers.filter((m) => !selectedManagerIds.includes(m.id));
  const availableDirectorMembers = activeMembers.filter((m) => !selectedDirectorIds.includes(m.id));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Please enter a department name');
      return;
    }

    if (nameError) {
      toast.error(nameError);
      return;
    }

    const submitData: DepartmentFormData = {
      ...formData,
      manager_ids: selectedManagerIds,
      director_ids: selectedDirectorIds,
    };

    if (departmentLogo) {
      submitData.department_logo = departmentLogo;
    }

    if (mode === 'edit') {
      updateMutation.mutate(submitData);
    } else {
      createMutation.mutate(submitData);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={mode === 'edit' ? 'Edit Department' : 'Add Department'}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Department Name */}
        <Input
          label="Department Name"
          name="name"
          value={formData.name}
          onChange={handleNameChange}
          onBlur={handleNameBlur}
          placeholder="e.g., Engineering"
          required
          error={nameError || undefined}
          helperText={isCheckingName ? 'Checking name...' : undefined}
        />

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-body mb-1">
            Description
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
            placeholder="Brief description of the department..."
            rows={3}
            className="block w-full rounded-md border-strong shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border text-heading bg-surface"
          />
        </div>

        {/* Directors */}
        <div>
          <label className="block text-sm font-medium text-body mb-1">
            Directors
          </label>

          {selectedDirectorIds.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {selectedDirectorIds.map((directorId) => {
                const member = members.find((m) => m.id === directorId);
                if (!member) return null;
                return (
                  <span
                    key={directorId}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm bg-status-accent text-status-accent border border-status-accent"
                  >
                    {member.first_name} {member.last_name}
                    <button
                      type="button"
                      onClick={() => handleRemoveDirector(directorId)}
                      className="opacity-70 hover:opacity-100"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}

          <SearchableSelect
            value=""
            onChange={handleAddDirector}
            options={[
              { value: '', label: 'Select a member...' },
              ...availableDirectorMembers.map((member) => ({
                value: String(member.id),
                label: `${member.first_name} ${member.last_name}`,
              })),
            ]}
            placeholder="Search members to add..."
          />
          <p className="text-xs text-meta mt-1">
            Directors lead the department above the manager level. Optional.
          </p>
        </div>

        {/* Managers */}
        <div>
          <label className="block text-sm font-medium text-body mb-1">
            Managers
          </label>

          {/* Selected managers list */}
          {selectedManagerIds.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {selectedManagerIds.map((managerId) => {
                const member = members.find((m) => m.id === managerId);
                if (!member) return null;
                return (
                  <span
                    key={managerId}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800"
                  >
                    {member.first_name} {member.last_name}
                    <button
                      type="button"
                      onClick={() => handleRemoveManager(managerId)}
                      className="text-blue-400 hover:text-blue-600"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}

          {/* Add manager dropdown */}
          <SearchableSelect
            value=""
            onChange={handleAddManager}
            options={[
              { value: '', label: 'Select a member...' },
              ...availableManagerMembers.map((member) => ({
                value: String(member.id),
                label: `${member.first_name} ${member.last_name}`,
              })),
            ]}
            placeholder="Search members to add..."
          />
          <p className="text-xs text-meta mt-1">
            Managers can be any active member.
          </p>
        </div>

        {/* Department Logo */}
        <div>
          <label className="block text-sm font-medium text-body mb-2">Department Logo</label>
          <div className="flex items-center space-x-4">
            {mode === 'edit' && currentImageUrl && (
              <div className="flex-shrink-0">
                <img
                  src={currentImageUrl}
                  alt="Current"
                  className="w-16 h-16 rounded-full object-cover"
                />
              </div>
            )}
            <div className="flex-1">
              <input
                type="file"
                id="department_logo"
                name="department_logo"
                accept="image/*"
                onChange={handleFileChange}
                className="block w-full text-sm text-meta file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900/30 dark:file:text-blue-300"
              />
              <p className="text-xs text-meta mt-1">
                JPG or PNG, max 5MB. Image will be cropped to 160x160px.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4">
          <Button type="button" variant="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isLoading}>
            {mode === 'edit' ? 'Update Department' : 'Create Department'}
          </Button>
        </div>
      </form>

      {/* Role-swap Confirmation (overlay sits above the modal) */}
      {pendingSwap && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[60]">
          <div className="bg-surface-elevated rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-heading mb-2">Change role</h3>
            <p className="text-sm text-meta mb-6">
              <strong>{pendingSwap.memberName}</strong> is currently selected as{' '}
              <strong>{pendingSwap.from}</strong>. Assigning them as{' '}
              <strong>{pendingSwap.to}</strong> will remove the {pendingSwap.from} selection.
            </p>
            <div className="flex justify-end space-x-3">
              <Button type="button" variant="secondary" onClick={() => setPendingSwap(null)}>
                Cancel
              </Button>
              <Button type="button" onClick={confirmSwap}>
                Continue
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};
